import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button/Button';
import { CountBadge } from '@/design_system/CountBadge';
import Stack from '@/design_system/Stack/Stack';
import Tabs, { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import { useCreateDraftRequest, useRequests } from '@/models/request';
import { hasPermission } from '@/models/user';
import { useCurrentOrganization, useCurrentSession, useFlags } from '@/services/auth';
import { useResetSimpleBlocker } from '@/utils/navigation';

import { BulkActionBar } from './components/BulkActionBar';
import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';
import { formatServiceTypeFilter } from './Filters/utils';
import { useRequestSelection } from './hooks/useRequestSelection';
import { AllTab } from './Tabs/AllTab';
import { getAppliedRequestsSearch, REQUESTS_PER_PAGE } from './Tabs/common';
import { ToDoTab } from './Tabs/ToDoTab';
import { WaitingTab } from './Tabs/WaitingTab';

const Requests = () => {
  const { t } = useLingui();
  const { flags } = useFlags();

  const isBulkActionsEnabled = !!flags['enable-bulk-actions'];

  // Clear router blocker state after coming from requests/new
  useResetSimpleBlocker();

  const filters = useFiltersState();

  const requestsParams = {
    limit: REQUESTS_PER_PAGE,
    offset: (filters.page.page - 1) * REQUESTS_PER_PAGE,
    search: getAppliedRequestsSearch(filters.search.debouncedSearch),
    requestorTypes: filters.requestorTypes.requestorTypes,
    displayedSteps: filters.displayedSteps.displayedSteps,
    collaborators: filters.collaborators.collaborators.map(({ id }) => id),
    tasks: filters.tasks.tasks,
    supervisors: filters.supervisors.supervisors.map(({ id }) => id),
    articleServiceChoices: formatServiceTypeFilter(filters.serviceTypes.serviceTypes),
    warrantyTypes: filters.warrantyTypes.warrantyTypes,
    storeIds: filters.stores.stores.map(({ id }) => id),
    workshopIds: filters.workshops.workshops.map(({ id }) => id),
    stockLocationIds: filters.stockLocations.stockLocations.map(({ id }) => id),
    brandIds: filters.brands.brands.map(({ id }) => id),
    createdAtFrom: filters.createdAt.createdAt?.start ?? undefined,
    createdAtTo: filters.createdAt.createdAt?.end ?? undefined,
    subLocations: filters.subLocations.subLocations.map((option) => option.id),
    tab: filters.tab.tab,
    sortField: filters.sort.sort[filters.tab.tab].field,
    sortOrder: filters.sort.sort[filters.tab.tab].order,
  };

  const {
    data: { requests = [], meta } = {},
    isLoading: isRequestsLoading,
    isPlaceholderData: isRequestsPlaceholderData,
    isError: isRequestsError,
  } = useRequests(requestsParams, { keepPreviousData: true });

  // The selection is scoped to the displayed page: any change of tab, page, search, filters or
  // sort resets it
  const selection = useRequestSelection({ requests, resetKey: JSON.stringify(requestsParams) });

  const navigate = useNavigate();

  const {
    mutateAsync: createRequest,
    isPending: isPendingCreateRequest,
    isSuccess: isSuccessCreateRequest,
  } = useCreateDraftRequest();

  const handleCreateRequest = async () => {
    const { id } = await createRequest();

    navigate(`/requests/new/${id}`);
  };

  const createRequestLabel = t({ id: 'requests.new', message: 'New request' });

  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const canCreateRequest =
    hasPermission(currentSession, 'create_client_request', {
      organizationId: currentOrganization?.id,
    }) ||
    hasPermission(currentSession, 'create_store_request', {
      organizationId: currentOrganization?.id,
    });

  return (
    <>
      <div className="header">
        <Title size="200" as="h1" flex={1}>
          <Trans id="requests.title">Requests</Trans>
        </Title>
        {canCreateRequest && (
          <>
            <Button
              className="is-hidden-mobile"
              size="medium"
              onPress={handleCreateRequest}
              isLoading={isPendingCreateRequest || isSuccessCreateRequest}
              dataTrackingId="requests.new"
            >
              <IconAdd />
              {createRequestLabel}
            </Button>
            <Button
              className="is-hidden-from-tablet"
              size="large"
              iconOnly
              onPress={handleCreateRequest}
              isLoading={isPendingCreateRequest || isSuccessCreateRequest}
              ariaLabel={createRequestLabel}
              tooltip={createRequestLabel}
              dataTrackingId="requests.new"
            >
              <IconAdd />
            </Button>
          </>
        )}
      </div>
      <div className="container container--list">
        <Stack gap="24px" style={{ height: '100%' }}>
          <Stack gap="1rem" style={{ flex: 1 }}>
            <FiltersStateContext value={filters}>
              <Filters />

              <Tabs
                gap="24px"
                selectedKey={filters.tab.tab}
                onSelectionChange={(tab) => filters.tab.setTab(tab as typeof filters.tab.tab)}
                style={{
                  flex: 1,
                  paddingBottom: selection.selectedIds.size > 0 ? 96 : 24,
                }}
              >
                <TabList>
                  <Tab id="to-do" aria-label={t({ id: 'requests.tabs.to-do', message: 'To do' })}>
                    <Trans id="requests.tabs.to-do">To do</Trans>
                    {!!meta && (
                      <CountBadge
                        count={meta.countTodo}
                        countFormat="1k"
                        variant="neutral"
                        isActive={filters.tab.tab === 'to-do'}
                      />
                    )}
                  </Tab>

                  <Tab
                    id="waiting"
                    aria-label={t({ id: 'requests.tabs.waiting', message: 'Waiting' })}
                  >
                    <Trans id="requests.tabs.waiting">Waiting</Trans>
                    {!!meta && (
                      <CountBadge
                        count={meta.countWaiting}
                        countFormat="1k"
                        variant="neutral"
                        isActive={filters.tab.tab === 'waiting'}
                      />
                    )}
                  </Tab>

                  <Tab id="all" aria-label={t({ id: 'requests.tabs.all', message: 'All' })}>
                    <Trans id="requests.tabs.all">All</Trans>
                    {!!meta && (
                      <CountBadge
                        count={meta.countAll}
                        countFormat="1k"
                        variant="neutral"
                        isActive={filters.tab.tab === 'all'}
                      />
                    )}
                  </Tab>
                </TabList>

                <TabPanel id="to-do" style={{ flex: 1 }}>
                  <ToDoTab
                    {...{
                      requests,
                      count: meta?.countTodo,
                      isLoading:
                        filters.tab.tab === 'to-do' &&
                        (isRequestsLoading || isRequestsPlaceholderData),
                      isError: filters.tab.tab === 'to-do' && isRequestsError,
                      selection,
                    }}
                  />
                </TabPanel>

                <TabPanel id="waiting" style={{ flex: 1 }}>
                  <WaitingTab
                    {...{
                      requests,
                      count: meta?.countWaiting,
                      isLoading:
                        filters.tab.tab === 'waiting' &&
                        (isRequestsLoading || isRequestsPlaceholderData),
                      isError: filters.tab.tab === 'waiting' && isRequestsError,
                      selection,
                    }}
                  />
                </TabPanel>

                <TabPanel id="all" style={{ flex: 1 }}>
                  <AllTab
                    {...{
                      requests,
                      count: meta?.countAll,
                      isLoading:
                        filters.tab.tab === 'all' &&
                        (isRequestsLoading || isRequestsPlaceholderData),
                      isError: filters.tab.tab === 'all' && isRequestsError,
                      selection,
                    }}
                  />
                </TabPanel>
              </Tabs>
            </FiltersStateContext>
          </Stack>
        </Stack>
      </div>
      {isBulkActionsEnabled && (
        <BulkActionBar requests={requests} selection={selection} tab={filters.tab.tab} />
      )}
    </>
  );
};

export default Requests;
